import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function EmployeeIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [formModal, setFormModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        cnic: '',
        phone: '',
        designation: '',
        address: '',
        salary: '',
        salary_date: ''
    });

    // Search state
    const [search, setSearch] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    useEffect(() => {
        fetchEmployees();
    }, [currentPage, perPage, search]);

    const fetchEmployees = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        let url = `${process.env.REACT_APP_API_URL}/employees?page=${currentPage}&per_page=${perPage}`;

        if (search) {
            url += `&search=${search}`;
        }

        axios.get(url, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => {
                if (res.data.employees) {
                    setEmployees(res.data.employees.data || []);
                    setTotal(res.data.employees.total || 0);
                    setCurrentPage(res.data.employees.current_page || 1);
                    setLastPage(res.data.employees.last_page || 1);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const handleSearch = (value) => {
        setSearch(value);
        setCurrentPage(1);

        // Debounce search
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(setTimeout(() => {
            fetchEmployees();
        }, 500));
    };

    const handleReset = () => {
        setSearch('');
        setCurrentPage(1);
        setTimeout(() => {
            fetchEmployees();
        }, 100);
    };

    const handleOpenModal = (employee = null) => {
        if (employee) {
            setIsEditing(true);
            setCurrentId(employee.id);
            setFormData({
                name: employee.name || '',
                cnic: employee.cnic || '',
                phone: employee.phone || '',
                designation: employee.designation || '',
                address: employee.address || '',
                salary: employee.salary || '',
                salary_date: employee.salary_date || ''
            });
        } else {
            setIsEditing(false);
            setCurrentId(null);
            setFormData({
                name: '',
                cnic: '',
                phone: '',
                designation: '',
                address: '',
                salary: '',
                salary_date: ''
            });
        }
        setFormModal(true);
    };

    const handleCloseModal = () => {
        setFormModal(false);
        setFormData({
            name: '',
            cnic: '',
            phone: '',
            designation: '',
            address: '',
            salary: '',
            salary_date: ''
        });
        setIsEditing(false);
        setCurrentId(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem("authToken");

        try {
            let response;
            if (isEditing) {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/employees/${currentId}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/employees`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
            }

            if (response.data) {
                handleCloseModal();
                fetchEmployees();

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: isEditing ? t('Employee updated successfully') : t('Employee created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            console.error('Error saving employee:', error);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.values(errors).flat().join('\n');
                Swal.fire({
                    icon: 'error',
                    title: t('validation error'),
                    text: errorMessages,
                    confirmButtonText: t('ok')
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response?.data?.message || t('Failed to save employee'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/employees/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAlertModal(false);
            fetchEmployees();

            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: t('Employee deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting employee:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete employee'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        }
    };

    const confirmDelete = (id) => {
        setCurrentId(id);
        setAlertModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0';
        return parseFloat(amount).toFixed(2);
    };

    // Generate pagination items
    const getPaginationItems = () => {
        let items = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(lastPage, currentPage + 2);

        if (endPage - startPage < 4) {
            if (startPage > 1) {
                startPage = Math.max(1, endPage - 4);
            }
            if (endPage < lastPage) {
                endPage = Math.min(lastPage, startPage + 4);
            }
        }

        if (startPage > 1) {
            items.push(
                <Pagination.First key="first" onClick={() => handlePageChange(1)} />
            );
            items.push(<Pagination.Ellipsis key="ellipsis-start" />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < lastPage) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            items.push(
                <Pagination.Last key="last" onClick={() => handlePageChange(lastPage)} />
            );
        }

        return items;
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage && newPage !== currentPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && employees.length === 0) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('employees')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('employees')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {/* Header with Add Button */}
                        <div className="mc-card-header d-flex justify-content-between align-items-center p-3">
                            <h5 className="mc-card-title mb-0">{t('employees list')}</h5>
                            {hasPermission("Employee create") && (

                                <ButtonComponent
                                    type="button"
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={() => handleOpenModal()}
                                >
                                    <i className="material-icons">add</i>
                                    {t('Add Employee')}
                                </ButtonComponent>
                            )}
                        </div>

                        {/* Search Section */}
                        <div className="p-3 border-bottom">
                            <Row className="align-items-end">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">{t('search')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('Search by name, phone or CNIC...')}
                                            value={search}
                                            onChange={(e) => handleSearch(e.target.value)}
                                            className="custom-input"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex gap-2">
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleReset}
                                        >
                                            <i className="material-icons me-1" style={{ fontSize: '16px' }}>refresh</i>
                                            {t('reset')}
                                        </ButtonComponent>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('name')}</th>
                                                    <th>{t('CNIC')}</th>
                                                    <th>{t('phone')}</th>
                                                    <th>{t('designation')}</th>
                                                    <th>{t('salary')}</th>
                                                    <th>{t('salary date')}</th>
                                                    <th>{t('address')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody >
                                                {employees.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-5">
                                                            <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                                                            <p className="mt-2">{t('no employees found')}</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    employees.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{((currentPage - 1) * perPage) + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>{item.name || '-'}</td>
                                                            <td>{item.cnic || '-'}</td>
                                                            <td>{item.phone || '-'}</td>
                                                            <td>{item.designation || '-'}</td>
                                                            <td>{formatCurrency(item.salary)}</td>
                                                            <td>{formatDate(item.salary_date)}</td>
                                                            <td>
                                                                <div style={{ maxWidth: '200px', whiteSpace: 'normal' }}>
                                                                    {item.address || '-'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Employee edit") && (

                                                                    <ButtonComponent
                                                                        type="button"
                                                                        className="material-icons edit"
                                                                        onClick={() => handleOpenModal(item)}
                                                                        title={t('edit')}
                                                                    >
                                                                        edit
                                                                    </ButtonComponent>
                                                                    )}
                                                                    {/* <ButtonComponent
                                                                        type="button"
                                                                        className="material-icons delete"
                                                                        onClick={() => confirmDelete(item.id)}
                                                                        title={t('delete')}
                                                                    >
                                                                        delete
                                                                    </ButtonComponent> */}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Bootstrap Pagination */}
                                        {total > 0 && (
                                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                                <div className="text-muted small">
                                                    {t('showing')} {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('entries')}
                                                </div>
                                                <div className="d-flex gap-3 align-items-center">
                                                    <Pagination size="sm" className="mb-0">
                                                        <Pagination.Prev
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        />
                                                        {getPaginationItems()}
                                                        <Pagination.Next
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                            disabled={currentPage === lastPage}
                                                        />
                                                    </Pagination>
                                                </div>
                                            </div>
                                        )}

                                        {/* Add/Edit Modal */}
                                        <Modal show={formModal} onHide={handleCloseModal} size="lg" centered>
                                            <Modal.Header closeButton className="p-3">
                                                <Modal.Title>
                                                    {isEditing ? t('Edit Employee') : t('Add New Employee')}
                                                </Modal.Title>
                                            </Modal.Header>
                                            <Form onSubmit={handleSubmit}>
                                                <Modal.Body>
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('name')}</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="name"
                                                                    value={formData.name}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter employee name')}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('CNIC')}</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="cnic"
                                                                    value={formData.cnic}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter CNIC number')}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('phone')}</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="phone"
                                                                    value={formData.phone}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter phone number')}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('designation')}</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    name="designation"
                                                                    value={formData.designation}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter designation')}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('salary')}</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    name="salary"
                                                                    value={formData.salary}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter salary amount')}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('salary date')}</Form.Label>
                                                                <Form.Control
                                                                    type="date"
                                                                    name="salary_date"
                                                                    value={formData.salary_date}
                                                                    onChange={handleInputChange}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={12}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>{t('address')}</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    name="address"
                                                                    value={formData.address}
                                                                    onChange={handleInputChange}
                                                                    placeholder={t('Enter address')}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Modal.Body>
                                                <Modal.Footer className="p-3">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={handleCloseModal}
                                                    >
                                                        {t('cancel')}
                                                    </ButtonComponent>
                                                    <ButtonComponent
                                                        type="submit"
                                                        className="btn btn-primary"
                                                    >
                                                        {isEditing ? t('update') : t('save')}
                                                    </ButtonComponent>
                                                </Modal.Footer>
                                            </Form>
                                        </Modal>

                                        {/* Delete Confirmation Modal */}
                                        <Modal show={alertModal} onHide={() => setAlertModal(false)} centered>
                                            <div className="mc-alert-modal p-4 text-center">
                                                <i className="material-icons" style={{ fontSize: '50px', color: 'red' }}>warning</i>
                                                <h3 className="mt-3">{t("Are you sure?")}</h3>
                                                <p>{t("Want to delete this employee?")}</p>
                                                <div className="d-flex justify-content-center gap-2 mt-3">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setAlertModal(false)}
                                                    >
                                                        {t('cancel')}
                                                    </ButtonComponent>
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={() => handleDelete(currentId)}
                                                    >
                                                        {t('delete')}
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        </Modal>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}